import { normaliseIcaoHex, normaliseRegistration, normaliseTrack } from '@/lib/parse';
import type { NormalisedTrack, ParsedPoint, ParsedTrack } from '@/lib/parse';

import { addDays, clampToReach, formatDay, utcDay } from './history';
import { fetchJson, queryString, SourceError } from './http';
import type {
  CostEstimate,
  HistoryReach,
  SearchQuery,
  SourceCapabilities,
  TrackCandidate,
  TrackIdentityHint,
  TrackSource,
} from './types';
import { splitRange } from './windows';

/**
 * Flightradar24's commercial API — bring your own key.
 *
 * `DATA-SOURCES.md` §3 sets the shape of this one, and two of its findings are
 * the whole design:
 *
 * **Two calls per flight, always.** `/flight-tracks` takes an `fr24_id` and
 * nothing else, so a pilot who knows their registration and the date still has
 * to find the id first through a summary query. That is not a quirk to paper
 * over — it is two credit charges, so it is two explicit steps in the UI with
 * an estimate in front of each. `search` is the first, `fetch` the second.
 *
 * **Never scrape.** This talks to the documented, paid API and nothing else.
 * There is no fallback to the website, no reverse-engineered endpoint, and no
 * "just this once" — the ground rule in `DATA-SOURCES.md` §3 is absolute, and
 * the absence of any such code path here is deliberate.
 *
 * The sandbox host is supported because it answers with fixed sample data and
 * consumes no credits, which makes it the only honest way to let a pilot check
 * their key and see the flow before spending anything.
 */

/** The documented production host. Overridable only so a test never needs a network. */
export const FR24_BASE_URL = 'https://fr24api.flightradar24.com';

/** The summary endpoint refuses a range wider than this. */
export const FR24_MAX_RANGE_DAYS = 14;

/**
 * The first day `/flight-summary` holds anything for, on any plan.
 *
 * FR24's own API description (the official `fr24api-mcp` server's tool text,
 * and the endpoint docs it mirrors): *"Data is available starting from
 * 2024-04-07 and will be extended further in the near future."* Historic
 * *positions* go back to 2016-05-11, but those are snapshots of the whole sky
 * at one instant, not a flight lookup by registration — so for this adapter,
 * which finds a flight through its summary, 2024-04-07 is the floor even on
 * the Advanced plan. Re-check it when FR24 extends the range.
 */
export const FR24_SUMMARY_FIRST_DAY = '2024-04-07';

export type Fr24Plan = 'explorer' | 'essential' | 'advanced';

/**
 * How far back each API plan reaches, from FR24's subscriptions page
 * (September 2026): Explorer 30 days, Essential 2 years, Advanced unlimited.
 * The API plans are a separate product from the website's Silver/Gold/Business
 * memberships, whose 90/365-day playback limits do not apply here.
 */
export const FR24_PLANS: Readonly<Record<Fr24Plan, { label: string; historyDays: number | null }>> =
  {
    explorer: { label: 'Explorer', historyDays: 30 },
    essential: { label: 'Essential', historyDays: 730 },
    advanced: { label: 'Advanced', historyDays: null },
  };

export function isFr24Plan(value: string | undefined): value is Fr24Plan {
  return value === 'explorer' || value === 'essential' || value === 'advanced';
}

const CAPABILITIES: SourceCapabilities = {
  search: true,
  /*
   * Depends on the plan the key belongs to, which is a setting rather than a
   * property of the adapter — see `history()`, which is where the search
   * screen gets the answer from.
   */
  historyDays: null,
  needsKey: true,
  metered: true,
};

/** One row of `/api/flight-summary/light`. Every field is optional: this is someone else's JSON. */
interface Fr24SummaryRow {
  fr24_id?: string;
  flight?: string;
  callsign?: string;
  type?: string;
  reg?: string;
  hex?: string;
  orig_icao?: string;
  dest_icao?: string;
  datetime_takeoff?: string;
  datetime_landed?: string;
  first_seen?: string;
  last_seen?: string;
  flight_ended?: boolean;
}

interface Fr24SummaryResponse {
  data?: Fr24SummaryRow[];
}

interface Fr24TrackPoint {
  timestamp?: string;
  lat?: number;
  lon?: number;
  alt?: number;
  gspeed?: number;
  vspeed?: number;
  track?: number;
  callsign?: string;
}

interface Fr24TrackRow {
  fr24_id?: string;
  tracks?: Fr24TrackPoint[];
}

export interface Fr24SourceOptions {
  apiKey: string;
  /**
   * Route through the sandbox, which returns fixed sample data and spends no
   * credits. The right default for "does my key work?", the wrong one for
   * "where did I fly on Tuesday?", so it is never on unless asked for.
   */
  sandbox?: boolean;
  /** The API plan the key belongs to, as the pilot set it. Unset means "not stated". */
  plan?: Fr24Plan;
  /** Fixed so a test is not a moving target. */
  now?: () => number;
  baseUrl?: string;
  fetchImpl?: typeof globalThis.fetch;
  /** Fixed so a test is not a moving target. */
  importedAt?: string;
  newId?: () => string;
}

/** `YYYY-MM-DDTHH:MM:SS`, UTC — the format the summary endpoint's date params take. */
function fr24Timestamp(date: Date): string {
  return date.toISOString().slice(0, 19);
}

/**
 * A string field from FR24, or `undefined` where it carries no value.
 *
 * FR24 writes `-` rather than omitting a field it has nothing for — a GA
 * aeroplane whose transponder sends no flight ID comes back with
 * `callsign: "-"`, not a missing key. Stored as-is that placeholder becomes a
 * flight labelled `-` in the flight list, which reads as a bug rather than as
 * the absence of data it is.
 */
function fr24Text(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === '' || trimmed === '-' ? undefined : trimmed;
}

function isoOrUndefined(value: string | undefined): string | undefined {
  if (value === undefined || value === '') {
    return undefined;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? new Date(parsed).toISOString() : undefined;
}

export class Fr24ApiSource implements TrackSource {
  readonly id = 'fr24-api';
  readonly label = 'Flightradar24 API';
  readonly capabilities = CAPABILITIES;

  private readonly options: Fr24SourceOptions;

  constructor(options: Fr24SourceOptions) {
    this.options = options;
  }

  private get base(): string {
    const root = (this.options.baseUrl ?? FR24_BASE_URL).replace(/\/+$/, '');
    return this.options.sandbox === true ? `${root}/api/sandbox` : `${root}/api`;
  }

  private get headers(): Record<string, string> {
    if (this.options.apiKey.trim() === '') {
      throw new SourceError(
        'no-key',
        'Add a Flightradar24 API token in Settings before searching. Nothing was sent.',
      );
    }
    return {
      Authorization: `Bearer ${this.options.apiKey.trim()}`,
      // The API versions itself through this header, not the path. Omitting it
      // is a 400 that reads like a malformed query.
      'Accept-Version': 'v1',
    };
  }

  /**
   * Flights FR24 holds for a registration over a date range.
   *
   * Uses `/flight-summary/light` rather than `/full`: the candidate list needs
   * identity, times and the two aerodromes, all of which `light` carries, and
   * it is the cheaper of the two.
   */
  async search(query: SearchQuery): Promise<TrackCandidate[]> {
    const registration = query.registration?.trim();
    if (registration === undefined || registration === '') {
      throw new SourceError(
        'unsupported',
        'Flightradar24 looks up flights by registration. Enter one to search.',
      );
    }

    const headers = this.headers;
    const candidates: TrackCandidate[] = [];

    /*
     * Checked before anything is sent: every summary request costs credits,
     * and a request for a date the plan cannot reach is refused *and* charged
     * for nothing. The sandbox answers any date with its fixed sample.
     */
    const reach = this.reach();
    const from = clampToReach(query.from, query.to, reach.earliestDay);
    if (from === undefined) {
      throw new SourceError('unsupported', `${reach.summary} Nothing was sent.`);
    }

    const windows = splitRange(from, query.to, FR24_MAX_RANGE_DAYS);
    for (const [index, window] of windows.entries()) {
      // Checked between windows only: a request already sent has already
      // spent its credits, so there is nothing to save by abandoning it.
      if (query.signal?.aborted === true) {
        break;
      }
      const url = `${this.base}/flight-summary/light${queryString({
        registrations: registration,
        flight_datetime_from: fr24Timestamp(window.from),
        flight_datetime_to: fr24Timestamp(window.to),
      })}`;
      const body = await fetchJson<Fr24SummaryResponse>(url, {
        service: 'Flightradar24',
        headers,
        ...(this.options.fetchImpl === undefined ? {} : { fetchImpl: this.options.fetchImpl }),
      });

      for (const row of body.data ?? []) {
        if (row.fr24_id === undefined || row.fr24_id === '') {
          continue;
        }
        const start = isoOrUndefined(row.datetime_takeoff) ?? isoOrUndefined(row.first_seen);
        const end = isoOrUndefined(row.datetime_landed) ?? isoOrUndefined(row.last_seen);
        const reg = fr24Text(row.reg);
        const callsign = fr24Text(row.callsign);
        const hex = fr24Text(row.hex);
        candidates.push({
          ref: row.fr24_id,
          ...(reg === undefined ? {} : { registration: normaliseRegistration(reg) }),
          ...(callsign === undefined ? {} : { callsign }),
          ...(start === undefined ? {} : { startTime: start }),
          ...(end === undefined ? {} : { endTime: end }),
          ...(fr24Text(row.orig_icao) === undefined ? {} : { departure: row.orig_icao }),
          ...(fr24Text(row.dest_icao) === undefined ? {} : { arrival: row.dest_icao }),
          ...(hex === undefined ? {} : { icaoHex: hex.toLowerCase() }),
          ...(fr24Text(row.type) === undefined ? {} : { aircraftType: row.type }),
          ...(row.flight_ended === undefined ? {} : { inProgress: !row.flight_ended }),
        });
      }
      query.onProgress?.(index + 1, windows.length);
    }

    // Newest first: a pilot searching a range is usually after the recent end
    // of it, and the list is read top-down.
    candidates.sort((a, b) => (b.startTime ?? '').localeCompare(a.startTime ?? ''));
    return candidates;
  }

  /**
   * One flight's positional track, by `fr24_id`. The second of the two credit
   * charges.
   *
   * `identity` matters more here than it looks. `/flight-tracks` answers with
   * an `fr24_id` and a list of positions — no registration, no hex, nothing
   * that says which aeroplane this is. The summary call that bought the id
   * *did* carry `reg` and `hex`, so without the hint the only identity left is
   * whatever ADS-B callsign happens to be on the points, and
   * `registrationFromCallsign` will only claim that as a registration when it
   * is a bare `ZK`/`VH` plus three letters. A transponder with no flight ID
   * entered transmits none at all, which is common in GA, and the flight then
   * lands as "Unknown aircraft" despite the pilot having just read `ZK-DFH` in
   * the candidate list. The hint closes that gap; it is not a fallback.
   */
  async fetch(ref: string, identity?: TrackIdentityHint): Promise<NormalisedTrack> {
    const url = `${this.base}/flight-tracks${queryString({ flight_id: ref })}`;
    const body = await fetchJson<Fr24TrackRow[] | Fr24TrackRow>(url, {
      service: 'Flightradar24',
      headers: this.headers,
      ...(this.options.fetchImpl === undefined ? {} : { fetchImpl: this.options.fetchImpl }),
    });

    const row = Array.isArray(body) ? body[0] : body;
    const rawPoints = row?.tracks ?? [];
    if (rawPoints.length === 0) {
      throw new SourceError(
        'not-found',
        `Flightradar24 returned no positions for flight ${ref}. The credit was still charged. The flight may be too old for the plan in use, or have no ADS-B coverage at all.`,
      );
    }

    const points: ParsedPoint[] = rawPoints.map((point) => ({
      tMs: point.timestamp === undefined ? Number.NaN : Date.parse(point.timestamp),
      lat: point.lat ?? Number.NaN,
      lon: point.lon ?? Number.NaN,
      // Feet and knots on the wire, which is what this app stores — no
      // conversion, and no assumption either: the API documents both.
      altFt: point.alt ?? Number.NaN,
      gsKt: point.gspeed ?? Number.NaN,
      trackDeg: point.track ?? Number.NaN,
    }));

    /*
     * The first point carrying a real callsign, not the first carrying the
     * key. FR24 writes its `-` placeholder per point, so a track whose opening
     * fixes are on the ground with no flight ID and whose later ones have one
     * would otherwise be read as `-`.
     */
    const callsign = rawPoints
      .map((point) => fr24Text(point.callsign))
      .find((value) => value !== undefined);

    const hinted = fr24Text(identity?.registration);
    const registration = hinted === undefined ? undefined : normaliseRegistration(hinted);
    const hintedHex = fr24Text(identity?.icaoHex);
    // `normaliseIcaoHex` answers '' for anything that is not an address.
    const icaoHex = hintedHex === undefined ? undefined : normaliseIcaoHex(hintedHex);

    const parsed: ParsedTrack = {
      source: 'fr24-api',
      points,
      /*
       * ADS-B altitude is pressure altitude against 1013.25 hPa, the same as
       * every FR24 file export. Labelled, never presented as a GPS height or
       * as what the altimeter read.
       */
      altitudeRef: 'baro',
      ...(callsign === undefined ? {} : { callsign }),
      ...(registration === undefined || registration === '' ? {} : { registration }),
      ...(icaoHex === undefined || icaoHex === '' ? {} : { icaoHex }),
      warnings: [],
      notes: [
        `Flightradar24 API${this.options.sandbox === true ? ' (sandbox)' : ''}, flight ${ref}: ${rawPoints.length} positions.`,
      ],
    };

    return normaliseTrack(parsed, {
      sourceRef: ref,
      ...(this.options.importedAt === undefined ? {} : { importedAt: this.options.importedAt }),
      ...(this.options.newId === undefined ? {} : { id: this.options.newId() }),
    });
  }

  /** Synchronous: FR24's reach is documented, not measured. */
  private reach(): HistoryReach {
    if (this.options.sandbox === true) {
      return { summary: 'The sandbox returns fixed sample data for any date.' };
    }
    const plan = this.options.plan === undefined ? undefined : FR24_PLANS[this.options.plan];
    const floor = FR24_SUMMARY_FIRST_DAY;
    if (plan === undefined) {
      return {
        earliestDay: floor,
        summary: `Flightradar24 flight data starts on ${formatDay(floor)}. Plan limits also apply: Explorer 30 days, Essential 2 years. Set the plan in Settings.`,
      };
    }
    if (plan.historyDays === null) {
      return {
        earliestDay: floor,
        summary: `Flightradar24 flight data starts on ${formatDay(floor)}.`,
      };
    }
    const today = utcDay(new Date(this.options.now?.() ?? Date.now()));
    const byPlan = addDays(today, -plan.historyDays);
    const earliestDay = byPlan > floor ? byPlan : floor;
    return {
      earliestDay,
      summary: `The ${plan.label} plan reaches back to ${formatDay(earliestDay)}.`,
    };
  }

  history(): Promise<HistoryReach> {
    return Promise.resolve(this.reach());
  }

  estimateCost(operation: 'search' | 'fetch', count: number): CostEstimate {
    const calls =
      operation === 'search'
        ? Math.max(1, Math.ceil(Math.max(1, count) / FR24_MAX_RANGE_DAYS))
        : Math.max(1, Math.round(count));

    if (this.options.sandbox === true) {
      return {
        calls,
        credits: 0,
        description: `${calls} sandbox ${calls === 1 ? 'request' : 'requests'}. The sandbox returns fixed sample data and consumes no credits.`,
      };
    }
    if (operation === 'search') {
      return {
        calls,
        credits: calls,
        description:
          calls === 1
            ? 'One flight-summary request. The plan in use decides what a summary costs in credits.'
            : `${calls} flight-summary requests. Flightradar24 caps one query at ${FR24_MAX_RANGE_DAYS} days, so a longer range is split across ${calls} of them. The plan in use decides what each costs in credits.`,
      };
    }
    return {
      calls,
      credits: calls,
      description: `${calls} flight-track ${calls === 1 ? 'request' : 'requests'}, one per flight. This is the second of the two charges per flight: the search above bought the flight id, this buys its positions.`,
    };
  }
}
